/*
 *  Copyright 2018 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the 'License');
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an 'AS IS' BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import https from 'https';
import getConfig from '../../config';
import { SET_FEATURE_FLAG } from '../types';
import uuid from 'uuid';

declare let adobeIMS: AdobeIMS;

class FeaturesService {
	_store;

	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * Retrieves current status of feature flags .
	 * @return object - feature flags
	 */
	static async fetchFlags(store, callWithoutIMS?: boolean) {
		const features = new FeaturesService(store);
		try {
			const res = await features._fetchFeatureFlags(callWithoutIMS);
			if (res) {
				window.featureFlags = res;
				window.cchome.util.joinFeatureSet();
				features._dispatchFeatureFlagCompleted(res);
			}
		} catch (error) {}
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: Object;

	constructor(store) {
		this._store = store;
	}

	/**
	 * Call server to retrieve current feature flags
	 */
	_fetchFeatureFlags(callWithoutIMS?: boolean): any {
		return this._performFeaturesServiceRequest(callWithoutIMS);
	}

	/**
	 * Dispatch feature flags
	 * @param {*} response
	 */
	_dispatchFeatureFlagCompleted(response: Object) {
		this._store.dispatch({
			type: SET_FEATURE_FLAG,
			payload: response,
		});
	}

	getHeaders(isAuthReq = true) {
		const headers: RequestHeaders = {
			'x-api-key': getConfig('CLIENT_ID'),
			'Content-Type': 'application/json',
			Accept: 'application/json',
			'x-request-id': uuid.v4(),
		};
		if (isAuthReq) {
			headers.authorization = `Bearer ${adobeIMS.getAccessToken()}`;
		}
		return headers;
	}

	/**
	 * Retrieves Links data for XD artifacts from Links Service.
	 * @param {assetID} asset id
	 * @return {Object}
	 */
	_performFeaturesServiceRequest(callWithoutIMS = false): any {
		const userProfile =
			!callWithoutIMS && window.adobeIMS && typeof window.adobeIMS.getUserProfile === 'function'
				? window.adobeIMS.getUserProfile()
				: undefined;
		const userId = (userProfile && (userProfile.authId || userProfile.userId) && 'me') || 'logged-out';
		const url = `v1/users/${userId}/featureflags`;
		return new Promise((resolve) => {
			const options = {
				hostname: getConfig('FEATURE_FLAG_URI'),
				path: `/${url}`,
				method: 'GET',
				headers: this.getHeaders(!callWithoutIMS),
				withCredentials: false,
				dataType: 'json',
				processData: false,
				contentType: 'application/vnd.adobe.search-request+json',
			};
			const request = https.request(options, (res) => {
				if (res.statusCode && res.statusCode >= 300) {
					const featureFlagService = new Error(`${res.statusCode}`);
					resolve(featureFlagService);
				} else {
					let data = '';
					// set the encoding to UTF8 to prevent decoding issues on certain browsers.
					res.setEncoding('utf8');
					res.on('data', (chunk) => (data += chunk));
					res.on('end', () => {
						// Success!
						try {
							resolve(JSON.parse(data));
						} catch (e) {
							resolve(e);
						}
					});
				}
			});

			request.on('error', (err) => {
				// on error, display the search error message
				window.cchome.util.logger.error(
					'Features Service',
					'Problem with Features Service request',
					err.toString(),
				);
				const featureFlagService = new Error('featureFlagService error');
				resolve(featureFlagService);
			});

			request.end();
		});
	}
}

export default FeaturesService;
